'use strict';

// Client helper for the user's Finance (BSV) account — the finance key derived at
// m/44'/0'/0'/0/0. Read-only chain state (balance / history / utxos) flows through the web3keys
// proxy (server/routes/wallet.js). The private key never leaves the browser; signing (Send) is
// done locally with bsv and only the raw tx is broadcast.

const api = require('../api');

const SATS_PER_BSV = 1e8;

// Format satoshis as a trimmed BSV string (e.g. 123456 -> "0.00123456", 0 -> "0").
function satsToBsv(sats) {
  const n = Math.max(0, Math.floor(Number(sats) || 0));
  if (n === 0) return '0';
  const s = (n / SATS_PER_BSV).toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
  return s;
}

async function summary(address) {
  if (!address) return { confirmed: 0, unconfirmed: 0, total: 0 };
  try { return await api.walletBalance(address); }
  catch (_) { return { confirmed: 0, unconfirmed: 0, total: 0, unavailable: true }; }
}

async function history(address) {
  if (!address) return [];
  try { return (await api.walletHistory(address)).items || []; }
  catch (_) { return []; }
}

async function utxos(address) {
  if (!address) return [];
  try { return (await api.walletUtxos(address)).utxos || []; }
  catch (_) { return []; }
}

const ADDR_RE = /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/; // base58 P2PKH (mainnet)
const DUST = 546;

function bsvToSats(str) {
  const n = Number(String(str).trim());
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * SATS_PER_BSV);
}

// Resolve a recipient (a BSV address or a paymail) to { address, scriptHex? }.
async function resolveRecipient(input, amountSats) {
  const raw = String(input || '').trim();
  if (ADDR_RE.test(raw)) return { address: raw, scriptHex: null };
  if (raw.includes('@')) {
    const r = await api.paymailResolve(raw.toLowerCase(), amountSats); // throws with a friendly error
    if (!r.address && !r.scriptHex) throw new Error('Could not resolve that paymail');
    return { address: r.address, scriptHex: r.scriptHex, paymail: r.paymail };
  }
  throw new Error('Enter a BSV address or a paymail');
}

// Build + sign a send tx entirely in the browser with the Finance key. `utxos` are the finance
// address's own P2PKH unspents, so each input's script is P2PKH of `changeAddress`. Returns the
// signed { txHex, fee, amountSats, toAddress }. Throws on insufficient funds / dust (bsv checks).
function buildSend({ bsv, financeKey, changeAddress, utxos, toAddress, toScriptHex, amountSats }) {
  if (!utxos || !utxos.length) throw new Error('No spendable balance yet');
  if (amountSats < DUST) throw new Error('Amount is below the dust limit');
  const script = bsv.Script.buildPublicKeyHashOut(changeAddress).toHex();
  const tx = new bsv.Transaction();
  for (const u of utxos) tx.from({ txId: u.txid, outputIndex: u.vout, satoshis: u.satoshis, script });
  tx.feePerKb(500);
  if (toScriptHex) tx.addOutput(new bsv.Transaction.Output({ script: bsv.Script.fromHex(toScriptHex), satoshis: amountSats }));
  else tx.to(toAddress, amountSats);
  tx.change(changeAddress);
  tx.sign(financeKey);
  const txHex = tx.serialize(); // checked: throws on bad fee / dust / insufficient funds
  return { txHex, fee: tx.getFee(), amountSats, toAddress: toAddress || null };
}

module.exports = { satsToBsv, bsvToSats, summary, history, utxos, resolveRecipient, buildSend, ADDR_RE, DUST, SATS_PER_BSV };
